import { useMemo } from 'react'

import { format, parseJSON } from 'date-fns'

import { IGridColumn } from 'components/common'
import { NotGeneratedCell } from 'components/suppliers/drivers/DriverInvoices/NotGeneratedCell'
import { DATE_TIME_FORMAT } from 'constants/index'
import {
  BookingStatus,
  DriverAllocationStatus,
  ManualDriverInvoiceCandidateDto,
} from 'generated/graphql'
import {
  formatJobCost,
  getAllocationStatusLabel,
  getBookingReference,
  getManualInvoiceBookingStatusLabel,
} from 'helpers'

export const useGetManualDriverInvoiceCandidatesColumns = () => {
  const columns: IGridColumn[] = useMemo(
    () => [
      {
        field: 'bookingId',
        headerName: 'FPL Number',
        description:
          'OnePoint booking reference. Search by the full reference (e.g. FPL001234) or the number alone.',
        minWidth: 150,
        flex: 1,
        valueFormatter: ({ value }) => getBookingReference(value as number),
      },
      {
        field: 'bookingStatus',
        headerName: 'Booking Status',
        description:
          'Only Delivered, Complete and Invoice bookings with every delivery signed for are listed. Cancelled bookings are excluded.',
        minWidth: 150,
        flex: 1,
        valueFormatter: ({ value }) => getManualInvoiceBookingStatusLabel(value as BookingStatus),
      },
      {
        field: 'invoiceId',
        headerName: 'Invoice Id',
        description:
          'Supplier invoice for this job. If an invoice already exists without an APR, only the APR is generated.',
        minWidth: 120,
        flex: 1,
        renderCell: ({ row }: { row: ManualDriverInvoiceCandidateDto }) =>
          row.invoiceId ?? (
            <NotGeneratedCell tooltip='No supplier invoice has been generated for this job yet.' />
          ),
      },
      {
        field: 'accountPayableRecordId',
        headerName: 'APR Id',
        description:
          'Account payable record created from the invoice. Bookings that already have an APR are complete and cannot be selected.',
        minWidth: 110,
        flex: 1,
        sortable: false,
        renderCell: ({ row }: { row: ManualDriverInvoiceCandidateDto }) => {
          if (row.accountPayableRecordId) return row.accountPayableRecordId

          return row.invoiceId ? (
            <NotGeneratedCell
              tooltip='The invoice exists but its APR was not created. Select the booking to generate the APR only.'
              color='error.main'
            />
          ) : (
            <NotGeneratedCell tooltip='No APR yet. It is created together with the invoice.' />
          )
        },
      },
      {
        field: 'deliveredAt',
        headerName: 'Delivered At',
        description: 'Date and time the final delivery on the booking was signed for.',
        minWidth: 150,
        flex: 1,
        valueFormatter: ({ value }) =>
          value && format(parseJSON(value as string), DATE_TIME_FORMAT),
      },
      {
        field: 'companyName',
        headerName: 'Company',
        description:
          'Owner or transport controller the invoice and APR are raised against. Jobs completed by their subcontractors are invoiced to them.',
        minWidth: 130,
        flex: 1,
      },
      {
        field: 'driverName',
        headerName: 'Driver',
        description:
          'Driver who completed the job: a subcontractor, or the owner or transport controller themselves.',
        minWidth: 130,
        flex: 1,
      },
      {
        field: 'allocationStatus',
        headerName: 'Allocation',
        description:
          'Driver response to the allocation in the Driver App. Jobs not accepted are skipped by automatic invoicing and can be invoiced here.',
        minWidth: 120,
        flex: 1,
        valueFormatter: ({ value }) => getAllocationStatusLabel(value as DriverAllocationStatus),
      },
      {
        field: 'courierExchangeLoadId',
        headerName: 'CX ID',
        description:
          'Courier Exchange Load ID. When present, the APR is created as SmartPay; otherwise EUR jobs go to HedgeFlows and all others to Telleroo.',
        minWidth: 100,
        flex: 1,
      },
      {
        field: 'cost',
        headerName: 'Job cost',
        description:
          'Driver charge plus any extra payment, in the currency agreed with the driver.',
        minWidth: 110,
        flex: 1,
        valueGetter: ({ row }: { row: ManualDriverInvoiceCandidateDto }) =>
          formatJobCost(row.cost, row.currencyCode),
        sortable: false,
      },
      { field: 'tariffName', headerName: 'Tariff', minWidth: 260, flex: 2 },
    ],
    [],
  )

  return columns
}
